import React, { useEffect, useState } from "react";
import { X, Loader2, FileSignature, Check } from "lucide-react";
import toast from "react-hot-toast";

import esignApi from "../../../api/esign";
import { createProposalAuthority } from "../api/prospectingReportsApi";

const TEAL = "#0f8d9b";

/**
 * Accepted proposal → listing authority. Picks the authority type, then the BE
 * creates a DRAFT e-sign authority pre-filled from the proposal (property, vendor
 * signers, price estimate, commission, marketing). The agent reviews and sends
 * it in the e-sign authority form; nothing is signed here.
 */
const CreateAuthorityModal = ({ proposal, onClose, onCreated }) => {
  const [authorities, setAuthorities] = useState([]);
  const [key, setKey] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    esignApi.listAuthorities()
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : [];
        setAuthorities(list);
        setKey((k) => k || (list.find((a) => a.key === "exclusive_sale") || list[0] || {}).key || "");
      })
      .catch(() => toast.error("Could not load the authority forms."))
      .finally(() => setLoading(false));
  }, []);

  const vendors = [
    ...(proposal.recipient_email ? [{ name: proposal.accepted_name || proposal.recipient_name, email: proposal.recipient_email }] : []),
  ];
  const carried = [
    proposal.full_address && `Property: ${proposal.full_address}`,
    vendors.length && `Vendor signer: ${vendors.map((v) => v.name || v.email).join(", ")}`,
    "Agent's price estimate (from the price guide)",
    "Commission (from the fees section)",
    "Marketing spend (from the quote)",
  ].filter(Boolean);

  const submit = async () => {
    if (!key) return;
    setSaving(true);
    try {
      const { data } = await createProposalAuthority(proposal.id, key);
      toast.success(`Authority created${data.prefilled?.length ? ` · ${data.prefilled.length} figures pre-filled` : ""}.`);
      if (data.unverified_signers?.length) {
        toast(`Please confirm the co-owner name(s) typed by the vendor: ${data.unverified_signers.join(", ")}`, { icon: "⚠️", duration: 8000 });
      }
      onCreated(data.envelope_id);
    } catch (e) {
      const d = e?.response?.data;
      if (d?.envelope_id) { onCreated(d.envelope_id); return; }
      toast.error(d?.detail || "Could not create the listing authority.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl w-full max-w-lg p-6 shadow-xl">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2"><FileSignature className="w-5 h-5" style={{ color: TEAL }} /> Create listing authority</h3>
          <button onClick={onClose} aria-label="Close"><X className="w-5 h-5 text-gray-500" /></button>
        </div>
        <p className="text-sm text-gray-500 mb-4">A draft authority is prepared in e-sign from this proposal. You review and send it there; nothing is signed yet.</p>

        <label className="block text-xs font-semibold text-gray-500 mb-1">Authority type</label>
        {loading ? <Loader2 className="w-4 h-4 animate-spin text-gray-400" /> : (
          <div className="space-y-2 mb-4">
            {authorities.map((a) => (
              <label key={a.key} className={`flex items-center gap-3 rounded-lg border px-3 py-2 cursor-pointer ${key === a.key ? "border-[#0f8d9b] bg-[#eef8f9]" : "border-gray-200"}`}>
                <input type="radio" name="authority" checked={key === a.key} onChange={() => setKey(a.key)} />
                <span className="text-sm font-semibold text-gray-800">{a.name}</span>
              </label>
            ))}
            {authorities.length === 0 && <p className="text-sm text-gray-400">No authority forms available.</p>}
          </div>
        )}

        <p className="text-xs font-semibold text-gray-500 mb-1">Carried across from the proposal</p>
        <ul className="text-sm text-gray-700 space-y-1 mb-5">
          {carried.map((c) => <li key={c} className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-[#2f9e6b] shrink-0" /> {c}</li>)}
        </ul>

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700">Cancel</button>
          <button onClick={submit} disabled={saving || !key}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-50" style={{ backgroundColor: "#2f9e6b" }}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSignature className="w-4 h-4" />} Create draft authority
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateAuthorityModal;
